import type { SessionActiveCompany, SessionCompany } from './authApi.js';

/*
 * How the client reads the tenancy half of /api/me: which company it should act AS, and what
 * state that company is in. Both live here, as plain functions over plain data, rather than
 * inside AuthContext.tsx / CompanyGate.tsx — vitest.web collects only src/**\/*.test.ts and runs
 * them in a node environment with no jsdom and no JSX transform (vitest.web.config.ts), so logic
 * that lives in a .tsx file cannot be asserted on at all. Same reason
 * src/pages/Admin/companyErrorCopy.ts is a .ts module.
 */

const STORAGE_PREFIX = 'velaigal.activeCompany.';

/**
 * The company to act as, given every active membership /api/me reported and whatever id was
 * last remembered for this user.
 *
 * VALIDATING THE REMEMBERED ID IS THE POINT of this function, not a nicety. The id is persisted
 * across sessions, and a membership can be revoked (or its company suspended) between two of
 * them. Sending a stale one back as ?companyId= does not fall back to anything server-side:
 * resolveCompanyCtx looks it up in the caller's OWN rows and throws 403 no_company when it is
 * absent (middleware/authz.ts), so every /hr/* route would fail for a recruiter who still has a
 * perfectly good second membership. Falling back to entry 0 — the same row /api/me itself calls
 * the active one, ordered by joined_at then company_id — leaves them working.
 */
export function pickActiveCompanyId(
  companies: readonly Pick<SessionCompany, 'id'>[],
  storedId: string | null,
): string | null {
  if (companies.length === 0) return null;
  if (storedId !== null && companies.some((c) => c.id === storedId)) return storedId;
  return companies[0].id;
}

/**
 * THE COMPANY THE SCREENS SHOULD TALK ABOUT — the picked one, not whichever /api/me happened to
 * report.
 *
 * /api/me's `company` field is membership row 0 (ordered joined_at, then company_id) and does not
 * honour ?companyId=: it is identical under every id the picker can send. Every OTHER request
 * carries the picked tenant, so a recruiter on two hiring teams who switched to the second one
 * was shown the first one's state — CompanyIncompleteNotice stayed silent while
 * POST /hr/jobs/:id/publish?companyId=<the second> answered 400 profile_incomplete, and would
 * have named the wrong employer had it fired. Resolving here rather than teaching /api/me a query
 * parameter keeps ?companyId= out of the one route it currently cannot reach, and the client
 * already holds everything needed: `companies` carries each entry's own status and isComplete.
 *
 * THE FALLBACK IS NOT A DEFAULT. An empty `companies` is the suspended / never-provisioned pair,
 * and `reported` is the only thing that can tell them apart (a suspended employer is absent from
 * the list by design and present here with status 'suspended'). The `?? companies[0]` beside it
 * mirrors pickActiveCompanyId's own entry-0 recovery and covers only the render between a session
 * load and AuthContext applying the id it picked; a live activeCompanyId is always one of these
 * ids, because setActiveCompany refuses anything else.
 */
export interface ActiveCompany {
  id: string;
  name: string;
  companyRole: 'owner' | 'member';
  status: string;
  isComplete: boolean;
}

export function resolveActiveCompany(
  companies: readonly SessionCompany[],
  activeCompanyId: string | null,
  reported: SessionActiveCompany | null,
): ActiveCompany | null {
  if (companies.length === 0) return reported;
  return companies.find((c) => c.id === activeCompanyId) ?? companies[0];
}

/*
 * Keyed by user id: two accounts sharing a browser must not inherit each other's choice, and an
 * id from one of them is guaranteed stale for the other. Every access is wrapped because
 * localStorage is absent in the test environment and throws outright in a browser configured to
 * block site data; a lost preference re-derives from pickActiveCompanyId on the next load, so
 * failing quietly is the correct behaviour rather than a swallowed error.
 */
function storage(): Storage | null {
  try {
    return typeof globalThis.localStorage === 'undefined' ? null : globalThis.localStorage;
  } catch { return null; }
}

export function readStoredCompanyId(userId: string): string | null {
  try { return storage()?.getItem(STORAGE_PREFIX + userId) ?? null; } catch { return null; }
}

export function writeStoredCompanyId(userId: string, companyId: string): void {
  try { storage()?.setItem(STORAGE_PREFIX + userId, companyId); } catch { /* blocked or full */ }
}

/**
 * What the HR app can and cannot do, read off the session company.
 *
 *   none        — no membership at all. Admin provisioning has not attached this account to a
 *                 company; there is nothing to post to and no self-serve way to fix it.
 *   suspended   — a membership whose EMPLOYER was suspended. /api/me reports it (via
 *                 findSuspendedCompanyMembership) precisely so this reads differently from
 *                 `none`: the company exists and can be named, its published jobs were closed
 *                 in the suspending transaction, and the recruiter did nothing wrong.
 *   incomplete  — active, but missing name/website/description. NOT a blocker: browsing,
 *                 reviewing and drafting all work. It stops exactly two things server-side —
 *                 publishing a job and generating its tasks, both 400 profile_incomplete.
 *   ready       — nothing to say.
 *
 * `status !== 'active'` rather than `=== 'suspended'`: companies.status admits exactly those two
 * values today (the zod enum on PATCH /api/admin/companies/:id, and the column's own CHECK), so
 * the two forms are equivalent now — but a third value added later must land in the blocking
 * branch, not silently be treated as a working tenant.
 */
export type CompanyState = 'none' | 'suspended' | 'incomplete' | 'ready';

export function companyState(company: Pick<SessionActiveCompany, 'status' | 'isComplete'> | null): CompanyState {
  if (!company) return 'none';
  if (company.status !== 'active') return 'suspended';
  return company.isComplete ? 'ready' : 'incomplete';
}
